<script setup lang="ts">
/**
 * WorkspaceRail — 데스크톱(lg↑) GNB 진행 레일 + 보관함 드롭다운.
 *
 * ## 왜 메가메뉴를 걷어냈나
 *
 * 구 GNB 는 5축을 전폭 메가메뉴로 **병렬 노출**했다. 세 가지가 어긋나 있었다:
 *  1. `WORKSPACE_NAV` 5축은 실제로 깔때기(입력→분석→종합→적재)인데 "5개 중 골라라"로 그려졌다.
 *  2. "내가 어디까지 왔나"가 헤더에 없어 재방문자가 각 화면에 들어가 봐야 했다.
 *  3. 대부분의 축은 하위 항목이 1개거나 같은 화면 앵커(workspaceNav.ts 가 이미 합쳐둔 것)라, 전폭
 *     dim + 5컬럼 패널을 띄워 항목 1개를 보여줬다. 분석 화면의 표·차트를 덮는 비용만 남았다.
 *
 * 그래서 축은 기본 **직접 링크**로 펴고, 진짜 선택지가 있는 곳만 300px 드롭다운으로 남긴다 —
 * 하위 화면이 2종 이상인 축(성적 분석: 정시/수시, `axis.items` 가 비어 있지 않음)과 보관함(`?kind=`
 * 필터). 어느 축이 드롭다운인지는 여기서 정하지 않고 `workspaceRail.ts` 의 `items` 파생 규칙을 따른다
 * (레일 전환 당시 "전부 직접 링크"로 가정해 수시 진입점이 사라졌던 회귀의 재발 방지).
 *
 * ## 상태 점은 잠금이 아니다
 *
 * `○` 는 "아직 비어 있음"이고 클릭해 즉시 들어갈 수 있다(043 SC-007 — 순차 잠금 시각 금지, 실제
 * 잠긴 축도 0개). 순서는 점이 아니라 **순번(①②③④) + 커넥터 라인**이 전달한다. 자세한 근거는
 * WorkspaceRailDot 의 주석 참조.
 *
 * 데이터는 `data/analysis/workspaceRail.ts`(선언) + `useWorkspaceProgress`(상태) 소유 — 이 파일은
 * 렌더만 한다.
 */
import { ChevronDown } from 'lucide-vue-next';
import { computed } from 'vue';
import { RouterLink, useRoute } from 'vue-router';

import WorkspaceRailDot from './WorkspaceRailDot.vue';
import { useGnbMenu } from '#/composables/useGnbMenu';
import { useWorkspaceProgress, type RailAxisView } from '#/composables/useWorkspaceProgress';
import {
  RAIL_ARCHIVE,
  isRailAxisActive,
  isRailItemActive,
  type RailSubItem,
} from '#/data/analysis/workspaceRail';

const emit = defineEmits<{
  /** 링크 이동 직후 — 셸이 열린 패널·모바일 메뉴를 정리한다. */
  navigate: [];
}>();

const route = useRoute();
const { axes } = useWorkspaceProgress();

/**
 * 드롭다운 열림 상태 — 드롭다운 축(`axis.key`)과 보관함(`ARCHIVE_KEY`)이 한 `useGnbMenu` 를 공유해
 * 동시에 하나만 열린다. hover 유예(150ms)·클릭 고정(pinned 분리 — "클릭이 영영 안 먹는" 함정)·ESC·
 * 외부 클릭·SSG 가드가 전부 필요하고, 그 주석들이 재발 방지 기록이다. 단순화하면 같은 함정을 다시 밟는다.
 */
const { isOpen, toggle, hoverOpen, hoverClose, hoverKeep, close } = useGnbMenu();

/** `useGnbMenu` 는 라벨로 상태를 들고 있다 — 보관함 키(축 key 와 겹치지 않는 값). */
const ARCHIVE_KEY = 'archive';

/** 쿼리의 `kind` 는 배열일 수 있다(vue-router) — 문자열일 때만 판정에 쓴다. */
const currentKind = computed(() =>
  typeof route.query.kind === 'string' ? route.query.kind : undefined,
);

/** 축 활성 — 드롭다운 축은 하위 항목 중 하나, 직접 링크 축은 대표 route 기준(하위 경로 포함). */
function isAxisActive(axis: Pick<RailAxisView, 'route' | 'items'>): boolean {
  return isRailAxisActive(axis, route.path, currentKind.value);
}

/** 드롭다운 항목 활성 — 정확 경로(+`?kind=`) 일치. */
function isSubItemActive(item: RailSubItem): boolean {
  return isRailItemActive(item.route, route.path, currentKind.value);
}

/** 보관함 그룹 활성 — 필터 항목 중 하나라도 현재 위치면 그룹도 활성. */
const archiveActive = computed(() => route.path.startsWith('/reports'));

function onNavigate(): void {
  close();
  emit('navigate');
}
</script>

<template>
  <nav class="hidden lg:flex lg:items-center lg:gap-1" aria-label="AI 입시분석 메뉴">
    <!-- 축 4개 — 순번·커넥터가 순서를, 점이 채움 여부를 전달한다. -->
    <template v-for="(axis, idx) in axes" :key="axis.key">
      <!--
        커넥터 — 앞 축이 채워졌으면 accent, 아니면 중성. 첫 축 앞에는 두지 않는다.
        `known` 이 false(비로그인·로딩)면 전부 중성이라 진행 표시가 나타나지 않는다.
      -->
      <span
        v-if="idx > 0"
        class="h-px w-4 flex-none transition-colors xl:w-6"
        :class="axes[idx - 1]?.state === 'filled' ? 'bg-accent' : 'bg-line'"
        aria-hidden="true"
      />
      <!-- 드롭다운 축 — 하위 화면이 2종 이상(성적 분석: 정시/수시). 보관함과 같은 열림 규약. -->
      <div
        v-if="axis.items.length > 0"
        class="relative"
        @mouseenter="hoverOpen(axis.key)"
        @mouseleave="hoverClose"
      >
        <button
          type="button"
          class="relative flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-[26px] text-[15px] font-semibold transition-colors hover:text-brand"
          :class="isAxisActive(axis) || isOpen(axis.key) ? 'text-brand' : 'text-body'"
          :aria-expanded="isOpen(axis.key)"
          aria-haspopup="true"
          :aria-controls="`ws-rail-${axis.key}-panel`"
          :title="axis.hint || undefined"
          @focus="hoverOpen(axis.key)"
          @click="toggle(axis.key)"
        >
          <span class="text-[11px] font-bold tabular-nums text-muted" aria-hidden="true">
            {{ axis.seq }}
          </span>
          {{ axis.label }}
          <WorkspaceRailDot :state="axis.state" />
          <ChevronDown
            :size="14"
            :stroke-width="2.4"
            class="transition-transform"
            :class="isOpen(axis.key) ? 'rotate-180' : ''"
          />
          <span
            v-if="isAxisActive(axis) || isOpen(axis.key)"
            class="absolute inset-x-2 bottom-[18px] h-[2px] rounded-full"
            :class="isOpen(axis.key) ? 'bg-brand' : 'bg-accent'"
            aria-hidden="true"
          />
        </button>
        <div
          v-show="isOpen(axis.key)"
          :id="`ws-rail-${axis.key}-panel`"
          class="absolute left-0 top-full w-[300px] rounded-card border border-line bg-white p-2 shadow-lg"
          @mouseenter="hoverKeep"
          @mouseleave="hoverClose"
        >
          <ul class="space-y-0.5">
            <li v-for="item in axis.items" :key="item.label">
              <RouterLink
                :to="item.route"
                class="block rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-bg-2"
                :aria-current="isSubItemActive(item) ? 'page' : undefined"
                @click="onNavigate"
              >
                <span
                  class="block text-[14.5px] font-bold"
                  :class="isSubItemActive(item) ? 'text-brand' : 'text-ink'"
                >
                  {{ item.label }}
                </span>
                <span v-if="item.desc" class="mt-0.5 block text-[13px] leading-snug text-muted">
                  {{ item.desc }}
                </span>
              </RouterLink>
            </li>
          </ul>
        </div>
      </div>

      <!-- 직접 링크 축 — 하위 항목이 1개거나 같은 화면 앵커. -->
      <RouterLink
        v-else
        :to="axis.route"
        class="relative flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-[26px] text-[15px] font-semibold transition-colors hover:text-brand"
        :class="isAxisActive(axis) ? 'text-brand' : 'text-body'"
        :aria-current="isAxisActive(axis) ? 'page' : undefined"
        :title="axis.hint || undefined"
        @click="onNavigate"
      >
        <span class="text-[11px] font-bold tabular-nums text-muted" aria-hidden="true">
          {{ axis.seq }}
        </span>
        {{ axis.label }}
        <WorkspaceRailDot :state="axis.state" />
        <!-- 현재 축 밑줄(골드) — 활성 표시. -->
        <span
          v-if="isAxisActive(axis)"
          class="absolute inset-x-2 bottom-[18px] h-[2px] rounded-full bg-accent"
          aria-hidden="true"
        />
      </RouterLink>
    </template>

    <!-- 구분선 — 여기부터는 여정 밖(결과 저장소). -->
    <span class="mx-2 h-4 w-px flex-none bg-line" aria-hidden="true" />

    <!-- 보관함 — 레일 밖 드롭다운(`?kind=` 필터 종류별 딥링크). -->
    <div class="relative" @mouseenter="hoverOpen(ARCHIVE_KEY)" @mouseleave="hoverClose">
      <button
        type="button"
        class="relative flex items-center gap-[3px] whitespace-nowrap py-[26px] text-[15px] font-semibold transition-colors hover:text-brand"
        :class="archiveActive || isOpen(ARCHIVE_KEY) ? 'text-brand' : 'text-body'"
        :aria-expanded="isOpen(ARCHIVE_KEY)"
        aria-haspopup="true"
        aria-controls="ws-rail-archive-panel"
        @focus="hoverOpen(ARCHIVE_KEY)"
        @click="toggle(ARCHIVE_KEY)"
      >
        {{ RAIL_ARCHIVE.label }}
        <ChevronDown
          :size="14"
          :stroke-width="2.4"
          class="transition-transform"
          :class="isOpen(ARCHIVE_KEY) ? 'rotate-180' : ''"
        />
        <span
          v-if="archiveActive || isOpen(ARCHIVE_KEY)"
          class="absolute inset-x-0 bottom-[18px] h-[2px] rounded-full"
          :class="isOpen(ARCHIVE_KEY) ? 'bg-brand' : 'bg-accent'"
          aria-hidden="true"
        />
      </button>

      <!--
        드롭다운 패널 — 전폭이 아니라 300px. 전체 화면 dim 을 두지 않는다(모달급 무게라 과하다):
        `useGnbMenu` 의 document 클릭 닫기가 클릭-아웃 역할을 이미 한다.
      -->
      <div
        v-show="isOpen(ARCHIVE_KEY)"
        id="ws-rail-archive-panel"
        class="absolute right-0 top-full w-[300px] rounded-card border border-line bg-white p-2 shadow-lg"
        @mouseenter="hoverKeep"
        @mouseleave="hoverClose"
      >
        <ul class="space-y-0.5">
          <li v-for="item in RAIL_ARCHIVE.items" :key="item.label">
            <RouterLink
              :to="item.route"
              class="block rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-bg-2"
              :aria-current="isSubItemActive(item) ? 'page' : undefined"
              @click="onNavigate"
            >
              <span
                class="block text-[14.5px] font-bold"
                :class="isSubItemActive(item) ? 'text-brand' : 'text-ink'"
              >
                {{ item.label }}
              </span>
              <span class="mt-0.5 block text-[13px] leading-snug text-muted">
                {{ item.desc }}
              </span>
            </RouterLink>
          </li>
        </ul>
      </div>
    </div>
  </nav>
</template>
